import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { UserPlus, GearSix } from "@phosphor-icons/react";

export default function SettingsPage() {
  const [settings, setSettings] = useState(null);
  const [users, setUsers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "operator", password: "" });

  const load = () => {
    api.get("/settings").then((r) => setSettings(r.data));
    api.get("/users").then((r) => setUsers(r.data));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const { data } = await api.put("/settings", settings);
      setSettings(data);
      toast.success("Settings saved");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const createUser = async () => {
    try {
      await api.post("/users", form);
      toast.success("User created");
      setCreating(false);
      setForm({ email: "", name: "", role: "operator", password: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deactivate = async (id) => {
    if (!confirm("Deactivate this user?")) return;
    try { await api.delete(`/users/${id}`); toast.success("Deactivated"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (!settings) return <div className="p-8">Loading…</div>;

  return (
    <div className="p-8 space-y-8">
      <div>
        <h1 className="font-display text-3xl font-bold text-slate-900 flex items-center gap-2">
          <GearSix size={26} weight="duotone" /> Settings
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Configure issuer details, numbering mode, and manage users.</p>
      </div>

      <div className="card-panel p-6 space-y-4" data-testid="issuer-form">
        <div className="font-display font-semibold">Business (issuer) details</div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>Business name</Label>
            <Input value={settings.business_name || ""} onChange={(e) => setSettings({ ...settings, business_name: e.target.value })} data-testid="setting-business-name" />
          </div>
          <div>
            <Label>Email</Label>
            <Input value={settings.business_email || ""} onChange={(e) => setSettings({ ...settings, business_email: e.target.value })} data-testid="setting-business-email" />
          </div>
          <div>
            <Label>Phone</Label>
            <Input value={settings.business_phone || ""} onChange={(e) => setSettings({ ...settings, business_phone: e.target.value })} data-testid="setting-business-phone" />
          </div>
          <div className="md:col-span-2">
            <Label>Address</Label>
            <Textarea rows={2} value={settings.business_address || ""} onChange={(e) => setSettings({ ...settings, business_address: e.target.value })} data-testid="setting-business-address" />
          </div>
          <div>
            <Label>Bank name</Label>
            <Input value={settings.bank_name || ""} onChange={(e) => setSettings({ ...settings, bank_name: e.target.value })} data-testid="setting-bank-name" />
          </div>
          <div>
            <Label>Bank A/C</Label>
            <Input value={settings.bank_account || ""} onChange={(e) => setSettings({ ...settings, bank_account: e.target.value })} data-testid="setting-bank-account" />
          </div>
          <div>
            <Label>IFSC</Label>
            <Input value={settings.bank_ifsc || ""} onChange={(e) => setSettings({ ...settings, bank_ifsc: e.target.value })} data-testid="setting-bank-ifsc" />
          </div>
          <div>
            <Label>Branch</Label>
            <Input value={settings.bank_branch || ""} onChange={(e) => setSettings({ ...settings, bank_branch: e.target.value })} data-testid="setting-bank-branch" />
          </div>
          <div>
            <Label>Numbering mode</Label>
            <Select value={settings.numbering_mode} onValueChange={(v) => setSettings({ ...settings, numbering_mode: v })}>
              <SelectTrigger data-testid="setting-numbering-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="party_initial">Party-initial (default) — e.g. A-2026-1047</SelectItem>
                <SelectItem value="sequential">Gap-free sequential — e.g. INV-2026-000001</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Button onClick={save} data-testid="save-settings-btn">Save changes</Button>
        </div>
      </div>

      <div className="card-panel p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="font-display font-semibold">Users</div>
          <Dialog open={creating} onOpenChange={setCreating}>
            <DialogTrigger asChild>
              <Button data-testid="new-user-btn"><UserPlus size={16} className="mr-2" />New user</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Create user</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-user-name" /></div>
                <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-user-email" /></div>
                <div>
                  <Label>Role</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Administrator</SelectItem>
                      <SelectItem value="operator">Operator</SelectItem>
                      <SelectItem value="reviewer">Reviewer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Password</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="new-user-password" /></div>
              </div>
              <DialogFooter><Button onClick={createUser} data-testid="confirm-new-user">Create</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
        <table className="w-full table-dense" data-testid="users-table">
          <thead className="bg-muted/60">
            <tr>
              <th className="text-left">Email</th>
              <th className="text-left">Name</th>
              <th className="text-left">Role</th>
              <th className="text-left">Active</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-border/60">
                <td>{u.email}</td>
                <td>{u.name}</td>
                <td className="text-xs uppercase">{u.role}</td>
                <td>{u.active === false ? <span className="text-rose-700 text-xs">disabled</span> : <span className="text-emerald-700 text-xs">active</span>}</td>
                <td className="text-right">
                  {u.active !== false && (
                    <Button size="sm" variant="ghost" className="text-rose-700" onClick={() => deactivate(u.id)} data-testid={`deactivate-${u.id}`}>Deactivate</Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
